/**
 * CoPilot Brain Input Component
 *
 * Message input with quota display for the Brain chat mode.
 */

import { ArrowUp, FileText, Globe, Lock, Plus, X, Zap } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button, cn } from '@tonnie/ui';
import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'wouter';

interface ICoPilotBrainInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onUpload: (file: File, kind: 'audio' | 'image' | 'document') => void;
  uploadsEnabled: boolean;
  isLoading: boolean;
  /** Name of the file attached to the composer (null = none). */
  attachmentName?: string | null;
  /** Remove the attached file. */
  onRemoveAttachment?: () => void;
  quotaUsed: number;
  quotaLimit: number | 'unlimited';
  isQuotaExceeded: boolean;
  /** Whether the "Search the web" toggle is on (BL-213 v2). */
  webSearchEnabled?: boolean;
  /** Toggle web-search consent; omit to hide the control entirely. */
  onToggleWebSearch?: (on: boolean) => void;
  /**
   * Where the upgrade CTAs link. Injected by the host app rather than
   * hard-coded, so each portal owns its own billing route.
   */
  billingHref?: string;
}

/** Derive the Companion upload kind from a picked file's MIME type. */
function fileKind(file: File): 'audio' | 'image' | 'document' | null {
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('image/')) return 'image';
  if (
    file.type === 'application/pdf' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'document';
  }
  return null;
}

export function CoPilotBrainInput({
  value,
  onChange,
  onSend,
  onUpload,
  uploadsEnabled,
  isLoading,
  attachmentName,
  onRemoveAttachment,
  quotaUsed,
  quotaLimit,
  isQuotaExceeded,
  webSearchEnabled = false,
  onToggleWebSearch,
  billingHref = '/billing',
}: ICoPilotBrainInputProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the textarea like ChatGPT: reset then size to content, capped so a
  // long draft scrolls instead of pushing the conversation up.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Enter sends; Shift+Enter is a newline. `isComposing` guards IME candidate
      // windows (Enter commits the candidate there, not the message).
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend]
  );

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // allow re-picking the same file
      if (!file) return;
      const kind = fileKind(file);
      if (kind) onUpload(file, kind);
    },
    [onUpload]
  );

  const isDisabled = isLoading || isQuotaExceeded;
  const canSend = !isDisabled && (value.trim().length > 0 || !!attachmentName);

  return (
    <div className="border-t border-copilot-border bg-copilot-surface px-4 py-3">
      {attachmentName && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-copilot-border bg-copilot-composer px-2 py-1.5 text-xs text-copilot-foreground">
          <FileText className="h-3.5 w-3.5 shrink-0 text-copilot-muted" aria-hidden="true" />
          <span className="flex-1 truncate">{attachmentName}</span>
          <button
            type="button"
            onClick={onRemoveAttachment}
            aria-label={t('copilot.removeAttachment')}
            className="text-copilot-muted hover:text-copilot-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}
      {/* One unified, rounded composer (ChatGPT-grade) on the --copilot surface. */}
      <div className="mb-2 flex items-end gap-1 rounded-2xl border border-copilot-border bg-copilot-composer px-2 py-1.5 focus-within:border-copilot-accent/60">
        {uploadsEnabled && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,audio/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handleFile}
              data-testid="copilot-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={isDisabled}
              aria-label={t('copilot.attachFile')}
              data-testid="copilot-attach-button"
              className="flex size-8 shrink-0 items-center justify-center rounded-full text-copilot-muted transition-colors hover:bg-copilot-accent/10 hover:text-copilot-foreground disabled:opacity-40"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
        {onToggleWebSearch && (
          <button
            type="button"
            onClick={() => onToggleWebSearch(!webSearchEnabled)}
            disabled={isDisabled}
            aria-pressed={webSearchEnabled}
            aria-label={t('copilot.webSearch.toggle')}
            title={t('copilot.webSearch.toggleHint')}
            data-testid="copilot-web-search-toggle"
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full text-copilot-muted transition-colors hover:bg-copilot-accent/10 hover:text-copilot-foreground disabled:opacity-40',
              webSearchEnabled && 'bg-copilot-accent/15 text-copilot-accent'
            )}
          >
            <Globe className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <textarea
          ref={taRef}
          rows={1}
          placeholder={t('copilot.askAnything')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          aria-label={t('copilot.aria.messageInput')}
          data-testid="copilot-message-input"
          className="max-h-40 min-h-8 flex-1 resize-none self-center bg-transparent px-1 py-1.5 text-sm leading-relaxed text-copilot-foreground placeholder:text-copilot-muted focus:outline-none disabled:opacity-60"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          aria-label={t('copilot.aria.sendMessage')}
          data-testid="copilot-send-button"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-copilot-accent text-copilot-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-30"
        >
          <ArrowUp className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {quotaLimit !== 'unlimited' ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {t('copilot.questionsRemaining', {
              count: Math.max(0, (quotaLimit as number) - quotaUsed),
            })}
          </p>
          {quotaUsed >= (quotaLimit as number) * 0.8 && !isQuotaExceeded && (
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
              <Link href={billingHref}>
                <Zap className="w-3 h-3 mr-1" aria-hidden="true" />
                {t('copilot.upgrade')}
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('copilot.unlimitedQuestions')}</p>
      )}

      {isQuotaExceeded && (
        <div className="mt-2 p-2 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-warning-accessible" aria-hidden="true" />
            <p className="text-xs font-medium text-warning-accessible">
              {t('copilot.dailyQuotaReached')}
            </p>
          </div>
          <Button size="sm" className="w-full mt-2 h-7 text-xs" asChild>
            <Link href={billingHref}>{t('copilot.upgradeToPro')}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
